import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash01 } from "@untitledui/icons";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { NativeSelect } from "@/components/base/select";
import { toast } from "sonner";
import { createInvoice } from "@/api/invoices";
import { getCustomers, getCustomer, type CustomerWithVehicles } from "@/api/customers";
import { getMyGarage } from "@/api/garage";
import type { CustomerDto, VehicleDto, GarageDto } from "@/types/dto";

interface LineItem {
  description: string;
  quantity: string;
  unitPrice: string;
}

interface FormValues {
  customerId: string;
  vehicleId: string;
  paymentType: "cash" | "card" | "bank_transfer";
  taxRate: string;
  isPaid: boolean;
  lines: LineItem[];
}

const today = new Date().toISOString().split("T")[0];

export default function InvoiceCreatePage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithVehicles | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleDto | null>(null);
  const [garage, setGarage] = useState<GarageDto | null>(null);
  const [saving, setSaving] = useState(false);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      customerId: "",
      vehicleId: "",
      paymentType: "cash",
      taxRate: "19",
      isPaid: false,
      lines: [{ description: "", quantity: "1", unitPrice: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "lines" });

  const fetchInitial = useCallback(async () => {
    const [custData, garageData] = await Promise.all([
      getCustomers({ limit: 100 }).catch(() => ({ data: [] })),
      getMyGarage().catch(() => null),
    ]);
    setCustomers(custData.data);
    setGarage(garageData);
  }, []);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const watchCustomerId = watch("customerId");
  const watchVehicleId = watch("vehicleId");

  // Fetch customer detail when selection changes
  useEffect(() => {
    if (!watchCustomerId) {
      setSelectedCustomer(null);
      setSelectedVehicle(null);
      setValue("vehicleId", "");
      return;
    }
    (async () => {
      try {
        const data = await getCustomer(watchCustomerId);
        setSelectedCustomer(data);
        setSelectedVehicle(null);
        setValue("vehicleId", "");
      } catch {
        setSelectedCustomer(null);
      }
    })();
  }, [watchCustomerId, setValue]);

  // Set selected vehicle when vehicle selection changes
  useEffect(() => {
    if (!watchVehicleId || !selectedCustomer) {
      setSelectedVehicle(null);
      return;
    }
    const v = selectedCustomer.vehicles.find((v) => v.id === watchVehicleId) ?? null;
    setSelectedVehicle(v);
  }, [watchVehicleId, selectedCustomer]);

  const watchLines = watch("lines");
  const watchTaxRate = watch("taxRate");

  const subtotal = watchLines.reduce((sum, line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unitPrice) || 0;
    return sum + qty * price;
  }, 0);

  const taxRate = parseFloat(watchTaxRate) || 0;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatAddress = (addr: CustomerDto["address"]) => {
    if (!addr) return "—";
    const parts = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  };

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const lines = values.lines.map((l) => {
        const qty = parseInt(l.quantity, 10) || 1;
        const price = parseFloat(l.unitPrice) || 0;
        return {
          description: l.description,
          quantity: qty,
          unitPrice: price,
          total: qty * price,
        };
      });

      const invoice = await createInvoice({
        customerId: values.customerId,
        dueDate: today,
        subtotal,
        taxAmount,
        total,
        status: values.isPaid ? "paid" : "pending",
        paymentMethod: values.paymentType,
        lines,
      });

      toast.success("Invoice created");
      navigate(`/invoices/${invoice.id}`);
    } catch {
      toast.error("Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const customerOptions = [
    { label: "Select customer...", value: "" },
    ...customers.map((c) => ({ label: `${c.firstName} ${c.lastName}`, value: c.id })),
  ];

  const vehicleOptions = [
    { label: "Select vehicle...", value: "" },
    ...(selectedCustomer?.vehicles ?? []).map((v) => ({
      label: `${v.make} ${v.model} (${v.year}) — ${v.licensePlate}`,
      value: v.id,
    })),
  ];

  return (
    <PageLayout title="New Invoice" subtitle="Create a new invoice">
      <div className="mb-6">
        <Button
          size="sm"
          color="link-gray"
          iconLeading={ArrowLeft}
          onClick={() => navigate("/invoices")}
        >
          Back to Invoices
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Invoice Details */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <h2 className="text-md font-semibold text-text-primary mb-4">Invoice Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-12 gap-y-3">
            {/* Left column — Date & VAT */}
            <div className="space-y-3">
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Date</span>
                <span className="text-sm text-text-primary">{today}</span>
              </div>
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">VAT No.</span>
                <span className="text-sm text-text-secondary">{garage?.vatNumber || "—"}</span>
              </div>
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">VAT / Tax Rate</span>
                <Controller
                  name="taxRate"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="19"
                      value={field.value}
                      onChange={field.onChange}
                      className="w-20 rounded-lg border border-border-secondary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary text-right outline-none focus:ring-2 focus:ring-fg-brand-primary"
                    />
                  )}
                />
              </div>
            </div>

            {/* Right column — Payment Type & Paid */}
            <div className="space-y-3 md:border-l md:border-border-secondary md:pl-12">
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Payment Type</span>
                <Controller
                  name="paymentType"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="cash"
                          checked={field.value === "cash"}
                          onChange={field.onChange}
                          className="size-4 accent-fg-brand-primary"
                        />
                        <span className="text-sm text-text-primary">Cash</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="card"
                          checked={field.value === "card"}
                          onChange={field.onChange}
                          className="size-4 accent-fg-brand-primary"
                        />
                        <span className="text-sm text-text-primary">Card</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="bank_transfer"
                          checked={field.value === "bank_transfer"}
                          onChange={field.onChange}
                          className="size-4 accent-fg-brand-primary"
                        />
                        <span className="text-sm text-text-primary">Bank Transfer</span>
                      </label>
                    </div>
                  )}
                />
              </div>
              <div className="flex items-center gap-6">
                <span className="text-sm text-text-tertiary w-28 shrink-0">Paid</span>
                <Controller
                  name="isPaid"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="size-4 rounded accent-fg-brand-primary"
                      />
                      <span className="text-sm text-text-secondary">Mark as Paid</span>
                    </label>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Vehicle */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <h2 className="text-md font-semibold text-text-primary mb-4">Customer & Vehicle</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="customerId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <NativeSelect
                  label="Customer"
                  options={customerOptions}
                  value={field.value}
                  onChange={field.onChange}
                  className={errors.customerId ? "ring-error-primary" : ""}
                />
              )}
            />
            <Controller
              name="vehicleId"
              control={control}
              render={({ field }) => (
                <NativeSelect
                  label="Vehicle"
                  options={vehicleOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!selectedCustomer}
                />
              )}
            />
          </div>

          {/* Auto-filled customer info */}
          {selectedCustomer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border-secondary">
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Address</p>
                <p className="text-sm text-text-secondary">{formatAddress(selectedCustomer.address)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Phone</p>
                <p className="text-sm text-text-secondary">{selectedCustomer.phone ?? "—"}</p>
              </div>
            </div>
          )}

          {/* Auto-filled vehicle info */}
          {selectedVehicle && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border-secondary">
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">License Plate</p>
                <p className="text-sm text-text-primary font-medium">{selectedVehicle.licensePlate}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Car Model</p>
                <p className="text-sm text-text-secondary">{selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-tertiary mb-1">Mileage</p>
                <p className="text-sm text-text-secondary">{selectedVehicle.mileage != null ? `${selectedVehicle.mileage.toLocaleString()} km` : "—"}</p>
              </div>
            </div>
          )}
        </div>

        {/* Line Items */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-md font-semibold text-text-primary">Line Items</h2>
            <Button
              type="button"
              size="sm"
              color="secondary"
              iconLeading={Plus}
              onClick={() => append({ description: "", quantity: "1", unitPrice: "" })}
            >
              Add Line
            </Button>
          </div>

          <div className="space-y-3">
            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-col sm:flex-row gap-3 items-start">
                <div className="flex-1 w-full">
                  <Controller
                    name={`lines.${index}.description`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: f }) => (
                      <Input
                        label={index === 0 ? "Description" : undefined}
                        placeholder="Service or item description"
                        isInvalid={!!errors.lines?.[index]?.description}
                        value={f.value}
                        onChange={f.onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-20">
                  <Controller
                    name={`lines.${index}.quantity`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: f }) => (
                      <Input
                        label={index === 0 ? "Qty" : undefined}
                        type="number"
                        placeholder="1"
                        value={f.value}
                        onChange={f.onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-32">
                  <Controller
                    name={`lines.${index}.unitPrice`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: f }) => (
                      <Input
                        label={index === 0 ? "Unit Price" : undefined}
                        type="number"
                        placeholder="0.00"
                        isInvalid={!!errors.lines?.[index]?.unitPrice}
                        value={f.value}
                        onChange={f.onChange}
                      />
                    )}
                  />
                </div>
                <div className="w-full sm:w-28 flex items-end">
                  <div className={index === 0 ? "mt-6" : ""}>
                    <p className="text-sm font-medium text-text-primary py-2">
                      {formatCurrency(
                        (parseFloat(watchLines[index]?.quantity) || 0) *
                        (parseFloat(watchLines[index]?.unitPrice) || 0)
                      )}
                    </p>
                  </div>
                </div>
                <div className={index === 0 ? "mt-6" : ""}>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      color="link-destructive"
                      iconLeading={Trash01}
                      onClick={() => remove(index)}
                      aria-label="Remove line"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6">
          <div className="flex flex-col items-end gap-2 text-sm">
            <div className="flex justify-between w-full max-w-xs">
              <span className="text-text-tertiary">Subtotal</span>
              <span className="text-text-primary">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between w-full max-w-xs">
              <span className="text-text-tertiary">VAT / Tax ({taxRate}%)</span>
              <span className="text-text-primary">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between w-full max-w-xs border-t border-border-secondary pt-2">
              <span className="font-semibold text-text-primary">Grand Total</span>
              <span className="font-semibold text-text-primary text-lg">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            color="secondary"
            size="md"
            onClick={() => navigate("/invoices")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            size="md"
            isLoading={saving}
            isDisabled={saving}
          >
            Create Invoice
          </Button>
        </div>
      </form>
    </PageLayout>
  );
}
